import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Select,
  Spacer, Spinner,
  Stack,
  Switch,
  Text,
  VStack
} from "@chakra-ui/react";
import CreatableSelect from "react-select";
import AsyncSelect from "react-select/async";
import ReactDatePicker from "react-datepicker";
import CampaignTypes from "./CampaignTypes";


const CampaignSettings = ({
                            medium,
                            template,
                            campaignType,
                            getTemplateValue,
                            handleSettingChange,
                            settingBgColor,
                            filterOptionEnabled,
                            setFilterOptionEnabled,
                            toast,
                            colorMode,
                            isEditing,
                            loadOptions,
                            onBackClicked,
                            onCancelClicked,
                            onSaveClicked,
                            savingCampaign
                          }) => {
  const [zipCodeInputValue, setZipCodeInputValue] = useState("")
  const campaignTypeConfig = CampaignTypes.find((c) => c.id === campaignType) || {};
  const filters = campaignTypeConfig.filters || [];

  const handleNewArrayValue = (newValue, filter) => {
    if (!filter.pattern || filter.pattern.test(newValue)) {
      const newOption = {
        value: newValue,
        label: newValue,
      };
      const updatedValues = [
        ...getTemplateValue(filter.id, template),
        newOption,
      ];
      handleSettingChange(filter.id, updatedValues);
    } else {
      toast({
        title: "Invalid Input",
        description: "The input does not match the required format.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    setZipCodeInputValue("")
    return "";
  }

  return (
    <VStack
      spacing={4}
      align="start"
      padding="20px"
      className="campaign_settings"
    >
      {filters
        .filter((x) => !x.condition || x.condition({ medium, template }))
        .map((filter) => {
          if (filter.type === "array") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {filter.required && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <CreatableSelect ml={4}
                  inputValue={zipCodeInputValue}
                  isMulti
                  placeholder="Enter values..."
                  value={getTemplateValue(filter.id, template)}
                  onChange={(values) => handleSettingChange(filter.id, values)}
                  onInputChange={(inputValue, { action }) => {
                    setZipCodeInputValue(inputValue);
                    if (action === "input-change" && inputValue.endsWith(",")) {
                      const newValue = inputValue.slice(0, -1).trim();
                      return handleNewArrayValue(newValue, filter)
                    }
                    return inputValue;
                  }}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      if (zipCodeInputValue === "") return
                      handleNewArrayValue(zipCodeInputValue.trim(), filter);
                      event.preventDefault();
                    }
                  }}

                  isValidNewOption={(inputValue) => filter.pattern.test(inputValue)}
                  getNewOptionData={(inputValue, optionLabel) => ({
                    value: inputValue,
                    label: optionLabel || inputValue,
                  })}
                  formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  styles={{
                    control: (base) => ({
                      ...base,
                      background: colorMode === "light" ? "gray.50" : "gray.700",
                      borderColor: colorMode === "light" ? "gray.300" : "gray.600",
                      "&:hover": {
                        borderColor: colorMode === "light" ? "gray.400" : "gray.500",
                      },
                      marginLeft:20,
                      marginRight:20
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: colorMode === "light" ? "white" : "gray.800",
                      boxShadow: "0px 10px 20px rgba(0,0,0,0.2)",
                      border: "1px solid",
                      borderColor: colorMode === "light" ? "gray.400" : "gray.500",
                      borderRadius: "4px",
                      zIndex: 9999,
                    }),
                    option: (base) => ({
                      ...base,
                      color: colorMode === "light" ? "black" : "white",
                    }),
                  }}
                />
              </FormControl>
            );
          }
          if (filter.type === "checkbox") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <Flex alignItems="center">
                  <FormLabel mb="0" flex="1" ml={4}>
                    {filter.label}
                    {filter.required && (
                      <Text as="span" color="red.500" ml={1}>
                        *
                      </Text>
                    )}
                  </FormLabel>
                  <Switch
                    id={filter.id}
                    isChecked={getTemplateValue(filter.id, template)}
                    onChange={(e) => handleSettingChange(filter.id, e.target.checked)}
                    size="lg"
                    ml={4}
                    mr={2}
                  />
                  <Text>{filter.description}</Text>
                </Flex>
              </FormControl>
            );
          }
          if (filter.type === "text") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <Flex align={"left"} direction="column" ml={4} mr={4}>
                  <FormLabel>
                    {filter.label}
                    {filter.required && (
                      <Text as="span" color="red.500" ml={1}>
                        *
                      </Text>
                    )}
                  </FormLabel>
                  <Input
                    value={getTemplateValue(filter.id, template)}
                    onChange={(e) => handleSettingChange(filter.id, e.target.value)}
                    variant="filled"
                    bg={colorMode === "light" ? "gray.50" : "gray.700"}
                    borderColor={colorMode === "light" ? "gray.300" : "gray.600"}
                    _hover={{
                      bg: colorMode === "light" ? "gray.200" : "gray.600",
                    }}
                    _focus={{ borderColor: "blue.500" }}
                  />
                </Flex>
              </FormControl>
            );
          }
          if (filter.type === "datetime") {
            return (
              <FormControl
                key={filter.id}
                align="start"
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {filter.required && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <Box width="100%" ml={4}>
                  <ReactDatePicker
                    id={"date-time-picker"}
                    selected={getTemplateValue(filter.id, template) ? new Date(getTemplateValue(filter.id, template)) : null}
                    onChange={(date) => handleSettingChange(filter.id, date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    timeCaption="time"
                    dateFormat="MMMM d, yyyy h:mm aa"
                    className="datepicker"
                    wrapperClassName={colorMode === "light" ? "light-mode" : "dark-mode"}
                    customInput={
                      <Input
                        variant="filled"
                        bg={colorMode === "light" ? "gray.50" : "gray.700"}
                      />
                    }
                  />
                </Box>
              </FormControl>
            );
          }
          if (filter.type === "radio") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {filter.required && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <RadioGroup
                  onChange={(value) => handleSettingChange(filter.id, value)}
                  value={getTemplateValue(filter.id, template)}
                  ml={4}
                >
                  <Stack direction="row" spacing={5} align="left">
                    {filter.options.map((o) => (
                      <Radio key={o.value} value={o.value}>
                        {o.name}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              </FormControl>
            );
          }
          if (filter.type === "rangeDate") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {filter.required && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <Flex align="left">
                  <Box flex="1" mr={2} align="start">
                    <FormLabel ml={4}>From</FormLabel>
                    <ReactDatePicker
                      selected={getTemplateValue(`${filter.id}`, template)[0] ? new Date(getTemplateValue(`${filter.id}`, template)[0]) : null}
                      onChange={(date) => handleSettingChange(`${filter.id}[0]`, date, "rangeDate")}
                      dateFormat="MMMM d, yyyy"
                      className="datepicker"
                      customInput={
                        <Input
                          variant="filled"
                          bg={colorMode === "light" ? "gray.50" : "gray.700"}
                          borderColor={colorMode === "light" ? "gray.300" : "gray.600"}
                          borderWeight="1px"
                          ml={4}
                        />
                      }
                    />
                  </Box>
                  <Box flex="1" align="start">
                    <FormLabel>To</FormLabel>
                    <ReactDatePicker
                      selected={getTemplateValue(`${filter.id}`, template)[1] ? new Date(getTemplateValue(`${filter.id}`, template)[1]) : null}
                      onChange={(date) => handleSettingChange(`${filter.id}[1]`, date, "rangeDate")}
                      dateFormat="MMMM d, yyyy"
                      className="datepicker"
                      customInput={
                        <Input
                          variant="filled"
                          bg={colorMode === "light" ? "gray.50" : "gray.700"}
                          borderColor={colorMode === "light" ? "gray.300" : "gray.600"}
                          borderWeight="1px"
                        />
                      }
                    />
                  </Box>
                </Flex>
              </FormControl>
            );
          }
          if (filter.type === "select") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {filter.required && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <Flex ml={4} mr={2}>
                  <Select
                    placeholder={filter.title}
                    value={getTemplateValue(filter.id, template) ?? ""}
                    onChange={(e) => handleSettingChange(`${filter.id}`, e.target.value, filter.type)}
                    mr={2}
                  >
                    {filter.options.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.name}
                      </option>
                    ))}
                  </Select>
                </Flex>
              </FormControl>
            );
          }
          if (filter.type === "rangeInt") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {filter.required && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <Flex ml={4} mr={2}>
                  <Select
                    placeholder={filter.title || "From"}
                    value={(Array.isArray(getTemplateValue(filter.id, template)) ? getTemplateValue(filter.id, template) : [getTemplateValue(filter.id, template)])[0] ?? ""}
                    onChange={(e) => handleSettingChange(`${filter.id}[0]`, e.target.value, filter.type)}
                    mr={2}
                  >
                    {Array.from({ length: filter.range[0][1] - filter.range[0][0] + 1 }, (_, i) => i + filter.range[0][0]).map((age) => (
                      <option key={age} value={age}>
                        {age}
                      </option>
                    ))}
                  </Select>
                  {filter.range[1] && (
                    <>
                      <Select
                        placeholder={filter.title || "To"}
                        value={getTemplateValue(filter.id, template)[1] || ""}
                        onChange={(e) => handleSettingChange(`${filter.id}[1]`, e.target.value, filter.type)}
                      >
                        {Array.from({ length: filter.range[1][1] - filter.range[1][0] + 1 }, (_, i) => i + filter.range[1][0]).map((age) => (
                          <option key={age} value={age}>
                            {age}
                          </option>
                        ))}
                      </Select>
                    </>
                  )}
                </Flex>
              </FormControl>
            );
          }
          return (
            <FormControl
              key={filter.id}
              bgColor={settingBgColor}
              mb={4}
              p={4}
              borderRadius="md"
            >
              <Flex align="center" ml={4}>
                <Text mb="0" flex="1" align="start">
                  {filter.label}
                </Text>
                <Switch
                  isChecked={filterOptionEnabled[filter.id]}
                  onChange={() => {
                    const enabled = !filterOptionEnabled[filter.id];
                    setFilterOptionEnabled({
                      ...filterOptionEnabled,
                      [filter.id]: enabled,
                    });
                    if (!enabled) {
                      handleSettingChange(filter.id, []);
                    }
                  }}
                  colorScheme="blue"
                  mb={2}
                  mr={2}
                />
              </Flex>
              <RadioGroup
                onChange={(value) => handleSettingChange(`${filter.id}.is_included`, value === "include")}
                value={getTemplateValue(filter.id + ".is_included", template) ? "include" : "exclude"}
                isDisabled={!filterOptionEnabled[filter.id]}
                ml={4}
              >
                <Stack direction="row" spacing={5} align="left">
                  <Radio value="include">Include</Radio>
                  <Radio value="exclude">Exclude</Radio>
                </Stack>
              </RadioGroup>
              <Box mt={2} ml={4} mr={2}>
                <AsyncSelect
                  isMulti
                  cacheOptions
                  defaultOptions={true}
                  value={getTemplateValue(filter.id + ".list", template) || []}
                  loadOptions={loadOptions(filter.id)}
                  onChange={(selectedOptions) => handleSettingChange(filter.id + ".list", selectedOptions.map((option) => ({ value: option.value, label: option.label })))}
                  isDisabled={!filterOptionEnabled[filter.id]}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      background: colorMode === "light" ? "gray.50" : "gray.700",
                      borderColor: colorMode === "light" ? "gray.300" : "gray.600",
                      "&:hover": {
                        borderColor: colorMode === "light" ? "gray.400" : "gray.500",
                      },
                      "&:disabled": {
                        background: colorMode === "light" ? "gray.200" : "gray.600",
                      },
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: colorMode === "light" ? "white" : "gray.800",
                      boxShadow: "0px 10px 20px rgba(0,0,0,0.2)",
                      border: "1px solid",
                      borderColor: colorMode === "light" ? "gray.400" : "gray.500",
                      borderRadius: "4px",
                      zIndex: 9999,
                    }),
                    option: (base) => ({
                      ...base,
                      color: colorMode === "light" ? "black" : "white",
                    }),
                  }}
                />
              </Box>
            </FormControl>
          );
        })}
      <Flex width="100%" justifyContent="space-between" mb={4}>
        {!isEditing && (
          <Button onClick={onBackClicked}>
            Back to Template
          </Button>
        )}
        <Spacer />
        <Flex align="right">
          <Button marginRight="10px" onClick={onCancelClicked}>
            Cancel
          </Button>
          <Button colorScheme="green" onClick={onSaveClicked} isDisabled={savingCampaign}>
            {savingCampaign ? <Spinner /> : "Save"}
          </Button>
        </Flex>
      </Flex>
    </VStack>
  );
};

export default CampaignSettings;
